import { MetaTags } from '@/components/meta-tags'
import { contact_path } from '@/lib/routes'
import { Link, usePage } from '@inertiajs/react'

export default function Privacy() {
  const {
    title,
    og_title,
    og_type,
    og_url,
    og_image,
    og_description,
    twitter_title,
    twitter_url,
    twitter_image,
    twitter_description,
  } = usePage().props
  return (
    <>
      <MetaTags
        title={title}
        description={og_description}
        og_title={og_title}
        og_type={og_type}
        og_url={og_url}
        og_image={og_image}
        og_description={og_description}
        twitter_title={twitter_title}
        twitter_url={twitter_url}
        twitter_image={twitter_image}
        twitter_description={twitter_description}
      />
      <div className="flex w-full flex-col items-center justify-start">
        <div className="container flex max-w-4xl flex-col items-center justify-start p-4 pt-[120px]">
          <article className="article-prose w-full">
            <h1>Privacy Policy</h1>
            <p>
              <strong>Last Updated:</strong> December 04, 2025
            </p>

            <h3>Overview</h3>
            <p>
              This Privacy Policy explains how picchat.ai (“App”) handles your
              data. We are committed to protecting your privacy and ensuring
              transparency regarding how your information is used.
            </p>

            <h3>Data Collection</h3>
            <p>
              <strong>Automatic Data Collection:</strong> The App does not
              automatically collect, store, transmit, or share any personal
              data, usage analytics, or telemetry with us or third parties. All
              processing performed by the App occurs locally on your device,
              except when you choose to connect to external AI providers using
              your own API keys.
            </p>
            <p>
              <strong>Voluntary Data You Provide:</strong> Both the App and our
              website may collect information when you voluntarily provide it,
              such as when you sign up for newsletters, updates, or waitlists.
              This may include your name, email address, platform preferences,
              use cases, and any other details you choose to share. This
              information is used solely to communicate with you and provide the
              services you requested.
            </p>

            <h3>Your Content</h3>
            <p>
              All images, prompts, files, and content you create, view, or
              modify using the App remain stored locally on your device. We have
              no access to this content and do not transmit, copy, or store it.
            </p>

            <h3>Third-Party AI Providers</h3>
            <p>
              The App supports a bring-your-own-key model for external AI
              services. If you choose to use these features:
            </p>
            <ul>
              <li>
                Your API keys are stored locally and never transmitted to us.
              </li>
              <li>
                Your prompts, images, and related data may be sent directly from
                your device to the AI provider you selected.
              </li>
              <li>
                We do not intercept, log, or access any data sent to or from
                third-party providers.
              </li>
              <li>
                Your use of external providers is subject to their privacy
                policies, data retention terms, and model-training rules.
              </li>
            </ul>
            <p>
              We encourage you to review each provider's policies before use.
            </p>

            <h3>Network Connections</h3>
            <p>
              The App makes outbound network requests only when you choose to:
            </p>
            <ul>
              <li>
                Connect to third-party AI providers using your own API keys
              </li>
              <li>
                Use optional features that connect to our webapp API, such as
                updating content within the App (e.g., style recipes) or
                submitting information you choose to provide
              </li>
            </ul>
            <p>
              The App does not perform background analytics, tracking,
              ad-related connections, or unauthorized network communication. All
              network requests are made only with your explicit action or
              consent.
            </p>

            <h3>Permissions</h3>
            <p>
              The App may request access to files, folders, or system features
              necessary for saving, loading, and editing images. These
              permissions are used solely to provide functionality and not for
              data collection.
            </p>

            <h3>Mac App Store and Platform Compliance</h3>
            <p>
              The App complies with all platform requirements, including Apple's
              App Store Review Guidelines. The App itself does not collect
              personal data, include analytics SDKs, or perform third-party
              tracking.
            </p>

            <h3>Security</h3>
            <p>
              Because data remains on your device, protection of your content
              depends on your device's security measures. We recommend keeping
              your operating system and the App up to date.
            </p>

            <h3>Children's Privacy</h3>
            <p>
              The App is not intended for children under 13. We do not knowingly
              collect or process data from children.
            </p>
            <p>
              Most third-party AI providers require users to be at least 13
              years old (or the minimum age required in their country) to create
              accounts and use their services. Some providers may require users
              to be 18 years old or older in certain regions, such as the
              European Economic Area. Users under 18 may need parental or legal
              guardian permission depending on the provider and jurisdiction.
            </p>
            <p>
              picchat.ai does not enforce age requirements beyond those applied
              by the app store from which you download the App. However, you are
              responsible for ensuring that your use of any third-party AI
              provider complies with that provider's age requirements and
              privacy policies. Different AI providers have varying age
              requirements and data handling practices, and it is your
              responsibility to review and comply with each provider's policies
              before connecting your API keys and using their services.
            </p>
            <p>
              For example, OpenAI requires users to be at least 13 years old (or
              the minimum age in their country), and users under 18 must have
              parental or legal guardian permission. Google Gemini requires
              users to be 13 or older in most countries, and 18 or older in the
              European Economic Area, Switzerland, or the United Kingdom. These
              requirements may vary by provider and region, so please review
              each provider's privacy policy and terms before use.
            </p>
            <p>
              If you are a parent or guardian and believe your child has
              provided personal information to us, please contact us using the
              contact form on the <Link href={contact_path()}>Contact</Link>{' '}
              page, and we will take steps to delete such information.
            </p>

            <h3>Changes to This Policy</h3>
            <p>
              We may update this Privacy Policy periodically. Changes will
              appear in the “Last Updated” date. Continued use of the App means
              you accept the updated policy.
            </p>

            <h3>Contact Us</h3>
            <p>
              For questions or concerns regarding privacy, use the contact form
              on the <Link href={contact_path()}>Contact</Link> page to reach
              out to us. We're here to help! Your feedback helps improve
              picchat.ai for you and everyone else.
            </p>
          </article>
        </div>
      </div>
    </>
  )
}
